import { useCallback, useState } from "react";

import CachedIcon from "@mui/icons-material/Cached";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ErrorIcon from "@mui/icons-material/Error";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Container from "@mui/material/Container";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import {
  CHUNK_LOAD_ERROR_MESSAGE,
  isChunkLoadError
} from "utils/lazy-with-retry.util";

interface ErrorDisplayProps {
  readonly error?: Error;
  readonly errorDetails?: string;
  readonly sessionId?: string;
  readonly routeName?: string;
  readonly isCentered?: boolean;
  readonly showReloadButton?: boolean;
}

export default function ErrorDisplay({
  error,
  errorDetails,
  sessionId,
  routeName,
  isCentered = false,
  showReloadButton = true
}: ErrorDisplayProps) {
  const [copied, setCopied] = useState(false);
  const isChunkError = isChunkLoadError(error);

  const handleReload = useCallback(() => {
    window.location.reload();
  }, []);

  const handleCopyErrorDetails = useCallback(async () => {
    if (errorDetails) {
      try {
        await navigator.clipboard.writeText(errorDetails);
        setCopied(true);
        // Reset copied state after 2 seconds
        setTimeout(() => {
          setCopied(false);
        }, 2000);
      } catch (err) {
        console.error("Failed to copy error details:", err);
      }
    }
  }, [errorDetails]);

  const content = (
    <>
      <Typography
        variant="h1"
        color="primary"
        sx={{
          display: "flex",
          alignItems: "center",
          mb: 4,
          lineHeight: 1.1
        }}
      >
        <ErrorIcon sx={{ mr: 2 }} fontSize="inherit" />
        We're sorry, but&nbsp;something unexpected happened
      </Typography>

      <Alert severity="error" sx={{ mt: 1, mb: 3 }}>
        <Typography variant="body3" fontWeight={700} gutterBottom>
          Failed to load {routeName || "this page"}
        </Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>
          {isChunkError
            ? CHUNK_LOAD_ERROR_MESSAGE
            : error?.message ||
              "An unexpected error occurred while loading this page."}
        </Typography>
      </Alert>

      <Stack>
        <Typography variant="body3" sx={{ lineHeight: 1.3, fontSize: "20px" }}>
          Try refreshing the page. If&nbsp;this keeps happening, copy
          the&nbsp;details below
          and&nbsp;reach&nbsp;out&nbsp;to&nbsp;our&nbsp;support team.
        </Typography>

        {sessionId && (
          <Typography
            variant="body3"
            sx={{
              mt: 2,
              fontFamily: "monospace"
            }}
          >
            Session ID: {sessionId}
          </Typography>
        )}
      </Stack>
      <Stack direction="row" spacing={4} mt={3}>
        {showReloadButton && (
          <Button
            variant="contained"
            startIcon={<CachedIcon />}
            onClick={handleReload}
          >
            {isChunkError ? "Retry" : "Reload Page"}
          </Button>
        )}
        <Button
          variant="outlined"
          sx={{ mt: 2 }}
          startIcon={<ContentCopyIcon />}
          onClick={handleCopyErrorDetails}
          size="small"
        >
          Copy Error Details
        </Button>
      </Stack>
      {copied && (
        <Typography variant="body2" sx={{ mt: 2, color: "success.main" }}>
          Copied!
        </Typography>
      )}
    </>
  );

  if (isCentered) {
    return (
      <Box
        sx={{
          position: "fixed",
          left: "50%",
          top: "50%",
          transform: "translate(-50%, -50%)"
        }}
      >
        {content}
      </Box>
    );
  }

  return (
    <Container>
      <Box sx={{ mt: 4 }}>{content}</Box>
    </Container>
  );
}
